import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PickingInfo } from '@deck.gl/core';
import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import { GeoJsonLayer } from '@deck.gl/layers';
import { InfoWindow, useMap } from '@vis.gl/react-google-maps';
import type { Feature, FeatureCollection, GeoJsonProperties } from 'geojson';

import type { CollectionSymbology, SymbolIcon } from '@atom/types/map';

import GeoJsonFeaturePopup, {
  type FeaturePopupHeader,
} from './GeoJsonFeaturePopup';

/** Click handler for a feature in a given collection. */
export type FeatureClickHandler = (
  feature: Feature,
  position: google.maps.LatLngLiteral,
) => void;

export type OverlayLayer = {
  collection: FeatureCollection;
  symbology: CollectionSymbology | null;
  fieldAliases: Record<string, string>;
  onFeatureClick: FeatureClickHandler | null;
  /**
   * If set, the feature popup shows only these property keys, in this order
   * (e.g. KML shows just `name`). Omit to show every property on the feature.
   */
  popupFields?: readonly string[];
  /**
   * If set, this property's value is shown as the popup's header title (e.g. KML
   * `name`) instead of as an attribute row.
   */
  popupTitleField?: string;
  /**
   * If set, this property's value is rendered as raw HTML (a full-width block)
   * in the built-in popup instead of as a plain attribute row — used for KML
   * descriptions, whose CDATA HTML the legacy `google.maps.KmlLayer` balloon
   * rendered verbatim.
   */
  popupHtmlField?: string;
  /**
   * Optional icon + name header shown above the attribute table in the built-in
   * popup (ArcGIS layers)
   */
  popupHeader?: FeaturePopupHeader;
};

type GeoJsonOverlayProps = {
  /**
   * The layers to render, in ascending z-order (earlier paints below later).
   * Each becomes its own deck.gl GeoJsonLayer.
   */
  layers: OverlayLayer[];
};

/** Base colors cycled per collection so stacked layers stay distinguishable. */
const LAYER_COLORS: ReadonlyArray<[number, number, number]> = [
  [0, 125, 239], // blue
  [230, 74, 60], // red
  [76, 175, 80], // green
  [255, 152, 0], // orange
  [156, 39, 176], // purple
  [0, 188, 212], // cyan
  [255, 193, 7], // amber
  [121, 85, 72], // brown
];

const FILL_ALPHA = 140;
const LINE_ALPHA = 220;
const FEATURE_POPUP_Z_INDEX = 1000;

/** A deck.gl dynamic-icon definition for one marker image. */
const toDeckIcon = (icon: SymbolIcon) => ({
  id: icon.url,
  url: icon.url,
  width: icon.width,
  height: icon.height,
  anchorX: icon.anchorX ?? icon.width / 2,
  anchorY: icon.anchorY ?? icon.height / 2,
  mask: false,
});

/**
 * Resolve a feature's marker image, or `null` if this collection has no icon
 * source. A per-feature `getIcon` (KML, where each placemark carries its own
 * image) takes precedence over a constant `icon` (ArcGIS picture markers).
 */
const iconResolver = (
  symbology: CollectionSymbology | null,
): ((feature: Feature) => SymbolIcon | null) | null => {
  const perFeatureIcon = symbology?.getIcon;
  const constantIcon = symbology?.icon;

  if (!perFeatureIcon && !constantIcon) {
    return null;
  }

  return (feature: Feature) =>
    perFeatureIcon ? perFeatureIcon(feature) : (constantIcon ?? null);
};

/**
 * deck.gl point-rendering props for a marker image, or `{}` for default colored
 * circles. In `icon` point mode a feature whose icon resolves to `null` is not
 * drawn — there is no circle sublayer to fall back to.
 */
const iconLayerProps = (
  resolve: ((feature: Feature) => SymbolIcon | null) | null,
) => {
  if (!resolve) {
    return {};
  }

  return {
    pointType: 'icon' as const,
    getIcon: (feature: Feature) => {
      const icon = resolve(feature);
      return icon ? toDeckIcon(icon) : null;
    },
    getIconSize: (feature: Feature) => resolve(feature)?.height ?? 0,
    iconSizeUnits: 'pixels' as const,
  };
};

/**
 * Line-width props for collections with an explicit per-feature width (KML
 * `<LineStyle><width>`, in pixels). Collections without one keep deck.gl's
 * defaults.
 */
const lineWidthProps = (symbology: CollectionSymbology | null) => {
  const getLineWidth = symbology?.getLineWidth;

  return getLineWidth === undefined || getLineWidth === null
    ? {}
    : { getLineWidth, lineWidthUnits: 'pixels' as const };
};

const POINT_GEOMETRY_TYPES = new Set(['Point', 'MultiPoint']);

const isPointFeature = (feature: Feature): boolean =>
  feature.geometry ? POINT_GEOMETRY_TYPES.has(feature.geometry.type) : false;

const withFeatures = (
  collection: FeatureCollection,
  features: Feature[],
): FeatureCollection => ({ ...collection, features });

/** Build one GeoJsonLayer for a collection's features in a given render band. */
const buildLayer = (
  fc: FeatureCollection,
  index: number,
  band: 'vector' | 'point',
  symbology: CollectionSymbology | null,
  onClick: (info: PickingInfo, index: number) => void,
) => {
  const [red, green, blue] = LAYER_COLORS[index % LAYER_COLORS.length];
  const resolve = iconResolver(symbology);

  return new GeoJsonLayer({
    id: `geojson-overlay-${index}-${band}`,
    data: fc,
    pickable: true,
    onClick: info => onClick(info, index),
    stroked: true,
    filled: true,
    pointRadiusMinPixels: 4,
    lineWidthMinPixels: 1,
    getFillColor: symbology?.getFillColor ?? [red, green, blue, FILL_ALPHA],
    getLineColor: symbology?.getLineColor ?? [red, green, blue, LINE_ALPHA],
    getPointRadius: 6,
    ...lineWidthProps(symbology),
    ...iconLayerProps(resolve),
  });
};

/**
 * Build deck.gl layers for all collections, split into two bands so every
 * collection's points draw above every collection's lines and polygons.
 */
const buildLayers = (
  layers: OverlayLayer[],
  onClick: (info: PickingInfo, index: number) => void,
) => {
  const layersForBand = (
    band: 'vector' | 'point',
    keep: (feature: Feature) => boolean,
  ) =>
    layers.map(({ collection, symbology }, index) =>
      buildLayer(
        withFeatures(
          collection,
          (collection.features as Feature[]).filter(keep),
        ),
        index,
        band,
        symbology,
        onClick,
      ),
    );

  return [
    ...layersForBand('vector', feature => !isPointFeature(feature)),
    ...layersForBand('point', isPointFeature),
  ];
};

/**
 * Repaint the overlay after a layer change; returns a canceller.
 *
 * On a non-interleaved vector map `setProps` updates the deck layers but the
 * surface only redraws on a camera move or an explicit `requestRedraw()`, so a
 * toggle with no pan/zoom stays invisible until we force the frame.
 */
const requestOverlayRedraw = (
  overlay: InstanceType<typeof GoogleMapsOverlay>,
): (() => void) => {
  let rafId = 0;

  const tick = () => {
    const instance = overlay as unknown as {
      _deck?: unknown;
      _overlay?: { requestRedraw?: () => void };
    };

    if (instance._deck) {
      instance._overlay?.requestRedraw?.();
    } else {
      rafId = requestAnimationFrame(tick);
    }
  };

  tick();
  return () => cancelAnimationFrame(rafId);
};

/**
 * Force the overlay's WebGL context to be released.
 *
 * `overlay.finalize()` frees deck's GPU resources but never calls
 * `loseContext()` (luma's `WebGLDevice.destroy` skips it), so the context leaks
 * until garbage collection. The Google map is recreated on every style/POI
 * change, so each swap would strand another live context and eventually hit the
 * browser's ~16-context ceiling. Call this before `finalize()` nulls `_deck`.
 */
const loseOverlayContext = (
  overlay: InstanceType<typeof GoogleMapsOverlay>,
): void => {
  const deck = (
    overlay as unknown as {
      _deck?: { getCanvas?: () => HTMLCanvasElement | null };
    }
  )._deck;

  const gl = deck?.getCanvas?.()?.getContext('webgl2');
  gl?.getExtension('WEBGL_lose_context')?.loseContext();
};

type PickedFeature = {
  properties: GeoJsonProperties;
  position: google.maps.LatLngLiteral;
  aliases: Record<string, string>;
  titleField?: string;
  htmlField?: string;
  header?: FeaturePopupHeader;
};

/**
 * Renders the layers on the Google Maps WebGL surface via a single
 * GoogleMapsOverlay; layer changes are pushed with `setProps`, not re-created.
 */
const GeoJsonOverlay = ({ layers }: GeoJsonOverlayProps) => {
  const map = useMap();
  const overlayRef = useRef<InstanceType<typeof GoogleMapsOverlay> | null>(
    null,
  );
  const hoveringRef = useRef(false);
  const [picked, setPicked] = useState<PickedFeature | null>(null);

  // Read by the stable click handler so it never rebuilds to capture fresh layers.
  const layersRef = useRef(layers);
  layersRef.current = layers;

  const handleClick = useCallback((info: PickingInfo, layerIndex: number) => {
    const activeLayers = layersRef.current;
    const feature = info.object as Feature | undefined;

    if (!feature || !info.coordinate) {
      setPicked(null);
      return;
    }

    const handler = activeLayers[layerIndex]?.onFeatureClick;

    if (handler) {
      setPicked(null);
      const [lng, lat] = info.coordinate;
      handler(feature, { lat, lng });
      return;
    }

    const layer = activeLayers[layerIndex];
    const aliases = layer?.fieldAliases ?? {};
    const popupFields = layer?.popupFields;
    const allProperties = feature.properties ?? {};
    const properties = popupFields
      ? popupFields
          .filter(field => field in allProperties)
          .reduce<GeoJsonProperties>(
            (acc, field) => ({ ...acc, [field]: allProperties[field] }),
            {},
          )
      : allProperties;
    const [lng, lat] = info.coordinate;

    setPicked({
      properties,
      position: { lat, lng },
      aliases,
      titleField: layer?.popupTitleField,
      htmlField: layer?.popupHtmlField,
      header: layer?.popupHeader,
    });
  }, []);

  // Rebuild deck layers only when `layers` change, not on every parent render.
  const deckLayers = useMemo(
    () => buildLayers(layers, handleClick),
    [layers, handleClick],
  );

  // Lets the create-overlay effect read the latest layers without re-running.
  const deckLayersRef = useRef(deckLayers);
  deckLayersRef.current = deckLayers;

  // Create the overlay once per map; layer updates go through the effect below.
  useEffect(() => {
    if (!map) {
      return undefined;
    }

    const handleHover = (info: PickingInfo) => {
      const isHovering = Boolean(info.object);

      if (isHovering === hoveringRef.current) {
        return;
      }

      hoveringRef.current = isHovering;
      map.setOptions({ draggableCursor: isHovering ? 'pointer' : null });
    };

    const overlay = new GoogleMapsOverlay({
      interleaved: false,
      layers: deckLayersRef.current,
      onHover: handleHover,
    });
    overlay.setMap(map);
    overlayRef.current = overlay;

    return () => {
      loseOverlayContext(overlay);
      overlay.finalize();
      overlayRef.current = null;

      if (hoveringRef.current) {
        hoveringRef.current = false;
        map.setOptions({ draggableCursor: null });
      }
    };
  }, [map]);

  // Push layer changes to the overlay, then repaint (see requestOverlayRedraw).
  useEffect(() => {
    const overlay = overlayRef.current;

    if (!overlay) {
      return undefined;
    }

    overlay.setProps({ layers: deckLayers });
    return requestOverlayRedraw(overlay);
  }, [deckLayers]);

  if (!picked) {
    return null;
  }

  return (
    <InfoWindow
      position={picked.position}
      zIndex={FEATURE_POPUP_Z_INDEX}
      headerDisabled
      onClose={() => setPicked(null)}
    >
      <GeoJsonFeaturePopup feature={picked} onClose={() => setPicked(null)} />
    </InfoWindow>
  );
};

export default GeoJsonOverlay;
